"use client";
import { useState } from "react";
import SEO from '../components/SEO';
import { createFAQSchema } from '../data/schemas';

const faqSections = [
  {
    title: "Booking Basics",
    items: [
      {
        question: "How far in advance should I book?",
        answer:
          "Most studio and on-location sessions confirm 4–6 weeks ahead. For branded launches or multi-day productions, reach out as soon as dates are locked so we can hold the full team.",
      },
      {
        question: "What does the retainer cover?",
        answer:
          "The 30% retainer secures your artist roster, timeline, and pre-production planning. It is non-refundable but transferrable once within 6 months if you reschedule with at least 2 weeks notice.",
      },
      {
        question: "Can you handle large teams?",
        answer:
          "Yes. We staff lead artists plus vetted assistants so talent flows chair to camera smoothly. Let us know cast size and call times and we'll scale accordingly.",
      },
    ],
  },
  {
    title: "Day-Of Logistics",
    items: [
      {
        question: "Do you travel outside Atlanta?",
        answer:
          "Absolutely. Travel fees cover transit, kit shipping if needed, and per-diem for longer bookings. Provide the address and schedule so we can quote it with your estimate.",
      },
      {
        question: "What do you need on set?",
        answer:
          "A clean table, outlet access, and steady lighting help us set up quickly. For exterior shoots, we bring pop-up stations but still need shade and power.",
      },
      {
        question: "How long does each face take?",
        answer:
          "Beauty looks average 45 minutes. Character or creative looks may run closer to 60. We'll build a custom timeline once we review references and wardrobe notes.",
      },
    ],
  },
  {
    title: "Products & Hygiene",
    items: [
      {
        question: "What products do you use?",
        answer:
          "Our kits mix pro staples from Danessa Myricks, Makeup Forever, and Pat McGrath alongside clean-skin prep from Tatcha and Embryolisse. All tools are sanitized between each client.",
      },
      {
        question: "Can you work with my personal products?",
        answer:
          "If you have dermatologist-prescribed skincare or specific sensitivity needs, we're happy to incorporate them. Share the items ahead of time so we can plan texture and wear testing.",
      },
    ],
  },
  {
    title: "Reschedules & Cancellations",
    items: [
      {
        question: "What happens if a shoot moves dates?",
        answer:
          "With 14+ days notice we'll apply your retainer to the new date. Inside that window we'll try to re-book the slot, but the original retainer becomes a studio credit if we can't.",
      },
      {
        question: "What if talent is running late?",
        answer:
          "We pad schedules, but delays may shorten service time to keep the production day on track. If you anticipate major shifts, text our producer as soon as possible so we can reorganize the run of show.",
      },
    ],
  },
];

export default function FAQ() {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  // Flatten all FAQs for schema
  const allFaqs = faqSections.flatMap(section =>
    section.items.map(item => ({
      question: item.question,
      answer: item.answer
    }))
  );

  const faqSchema = createFAQSchema(allFaqs);

  const toggleItem = (key: string) => {
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const expandAll = (sectionIndex: number) => {
    const newOpenItems = { ...openItems };
    faqSections[sectionIndex].items.forEach((_, itemIndex) => {
      const key = `${sectionIndex}-${itemIndex}`;
      newOpenItems[key] = true;
    });
    setOpenItems(newOpenItems);
  };

  return (
    <>
      <SEO
        title="FAQ - Frequently Asked Questions"
        description="Get answers to common questions about booking makeup services, day-of logistics, products, hygiene, and cancellation policies at Let's Face It Studio."
        keywords="makeup artist FAQ, bridal makeup booking, makeup service questions, professional makeup artist"
        schema={faqSchema}
      />
      <section className="fixed top-0 left-0 h-full w-full overflow-y-auto bg-gradient-to-b from-[#f1ede7]
      via-[#ece6de] to-[#e4ded4] text-black mt-[58px] xsm2:mt-[64px] sm:mt-[94px] md:mt-[123px] pb-[58px] xsm2:pb-[64px] sm:pb-[94px] md:pb-[123px] lg:pb-[150px]">
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-8">
        {faqSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-16">
            <div className="mb-8 flex items-center justify-between">
              <h2 className="text-2xl uppercase tracking-widest font-light">
                {section.title}
              </h2>
              <button
                onClick={() => expandAll(sectionIndex)}
                className="text-sm underline ml-8"
              >
                Expand All
              </button>
            </div>

            <div className="border-t border-gray-300">
              {section.items.map((item, itemIndex) => {
                const key = `${sectionIndex}-${itemIndex}`;
                const isOpen = openItems[key];
                
                return (
                  <div key={itemIndex} className="border-b border-gray-300">
                    <button
                      onClick={() => toggleItem(key)}
                      className="w-full flex items-start justify-between py-6 text-left"
                    >
                      <span className="text-sm uppercase tracking-wide pr-8 font-medium">
                        {item.question}
                      </span>
                      <span className="text-2xl font-light">
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="pb-6 pr-12 text-base leading-relaxed text-gray-700">
                        {item.answer}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div className="mt-16 text-center">
          <h3 className="mb-6 text-xl uppercase tracking-widest font-light">
            Still Have Questions?
          </h3>
          <a
            href="mailto:hello@letsfaceitcosmetics.com"
            className="inline-block bg-black px-12 py-4 text-sm uppercase tracking-widest text-white"
          >
            Contact Us
          </a>
        </div>
      </div>
      </section>
    </>
  );
}